import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Gamepad2, Youtube, Sparkles } from "lucide-react";
import { SECTIONS, LOGO_URL } from "../lib/constants";

const ICONS = {
  gaming: Gamepad2,
  youtube: Youtube,
  kids: Sparkles,
};

export default function Landing() {
  const [active, setActive] = useState(null); // section id currently expanded
  const navigate = useNavigate();

  const handleEnter = (href) => {
    setTimeout(() => navigate(href), 280);
  };

  return (
    <div
      data-testid="landing-root"
      className="relative w-full min-h-screen bg-[#FAFAFA] bg-grain overflow-hidden"
    >
      {/* Hero header overlay */}
      <div className="pt-20 sm:pt-24 pb-6 px-4 sm:px-6 lg:px-8 text-center relative z-10">
        <motion.img
          initial={{ opacity: 0, y: -20, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.6 }}
          src={LOGO_URL}
          alt="Arkweaver TFX logo"
          className="mx-auto h-20 sm:h-28 lg:h-32 w-auto drop-shadow-xl"
        />
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="mt-4 font-display font-black text-3xl sm:text-5xl lg:text-6xl tracking-tighter text-[#1A1414] text-balance"
        >
          Three worlds.{" "}
          <span className="text-[#E60000]">One mission.</span>
        </motion.h1>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-3 text-sm sm:text-base text-[#1A1414]/70 max-w-2xl mx-auto"
        >
          Tap a panel to enter — Gaming, YouTube Series or Kids Interactive. Powered
          by <strong className="text-[#8B0000]">The Edutainment Box</strong>.
        </motion.p>
      </div>

      {/* SPLIT PANELS — vertical on desktop, stacked horizontal on mobile */}
      <div
        data-testid="split-panels"
        className="relative z-10 mx-auto max-w-[1500px] px-3 sm:px-6 pb-8"
      >
        <div className="flex flex-col md:flex-row gap-3 md:gap-4 h-auto md:h-[68vh] min-h-[520px]">
          {SECTIONS.map((s) => {
            const Icon = ICONS[s.id];
            const isActive = active === s.id;
            const isDimmed = active && !isActive;

            return (
              <motion.button
                key={s.id}
                data-testid={`panel-${s.id}`}
                layout
                onMouseEnter={() => setActive(s.id)}
                onMouseLeave={() => setActive(null)}
                onClick={() => handleEnter(s.href)}
                initial={{ flex: 1 }}
                animate={{
                  flex: isActive ? 2.2 : isDimmed ? 0.6 : 1,
                }}
                transition={{ duration: 0.55, ease: [0.4, 0, 0.2, 1] }}
                className={`relative group overflow-hidden rounded-3xl text-left border-2 transition-colors ${
                  isActive
                    ? "border-[#E60000] shadow-arc-lg"
                    : "border-[#FF8080]/30 hover:border-[#FF8080]/60 shadow-arc"
                } bg-white min-h-[180px] md:min-h-0 cursor-pointer focus:outline-none focus-visible:ring-4 focus-visible:ring-[#E60000]/30`}
                style={{ "--accent": s.accent }}
                aria-label={`Enter ${s.label}`}
              >
                {/* Accent corner gradient */}
                <div
                  className="absolute inset-0 opacity-90 transition-opacity"
                  style={{
                    background: `linear-gradient(160deg, transparent 30%, ${s.accent}18 70%, ${s.accent}38 100%)`,
                  }}
                />
                {/* Stone pattern */}
                <div className="absolute inset-0 bg-stone-pattern opacity-40" />

                {/* Index marker */}
                <div className="absolute top-5 left-5 flex items-center gap-2 z-10">
                  <span className="text-[10px] font-display font-black uppercase tracking-[0.25em] text-[#1A1414]/50">
                    {s.eyebrow}
                  </span>
                </div>

                <div className="absolute top-5 right-5 z-10">
                  <div
                    className="w-11 h-11 rounded-full flex items-center justify-center text-white shadow-arc"
                    style={{ backgroundColor: s.accent }}
                  >
                    <Icon size={20} strokeWidth={2.4} />
                  </div>
                </div>

                {/* Vertical large label, visible when collapsed on desktop */}
                <div
                  className={`absolute inset-x-0 top-1/2 -translate-y-1/2 md:rotate-0 hidden md:flex flex-col items-center justify-center px-4 transition-opacity duration-300 ${
                    isActive ? "opacity-0" : "opacity-100"
                  }`}
                >
                  <span
                    className="font-display font-black text-4xl lg:text-5xl text-[#1A1414] writing-vertical tracking-tight"
                    style={{
                      writingMode: "vertical-rl",
                      transform: "rotate(180deg)",
                    }}
                  >
                    {s.label.toUpperCase()}
                  </span>
                </div>

                {/* Mobile / Expanded content */}
                <div
                  className={`relative z-10 flex flex-col justify-end h-full p-5 sm:p-7 md:p-8 transition-opacity duration-300 ${
                    isActive ? "md:opacity-100" : "md:opacity-100"
                  }`}
                >
                  <div className="md:hidden">
                    <h2 className="font-display font-black text-3xl text-[#1A1414] mb-1">
                      {s.label}
                    </h2>
                    <p className="text-sm text-[#1A1414]/70 mb-4">{s.description}</p>
                  </div>

                  <AnimatePresence mode="wait">
                    {(isActive || true) && (
                      <motion.div
                        key={isActive ? "open" : "closed"}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className={`hidden md:block ${
                          isActive ? "" : "md:hidden"
                        }`}
                      >
                        <h2 className="font-display font-black text-4xl lg:text-5xl text-[#1A1414] leading-[1.02] tracking-tighter mb-3">
                          {s.headline}
                        </h2>
                        <p className="text-sm lg:text-base text-[#1A1414]/75 max-w-md leading-relaxed">
                          {s.description}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <div className="mt-5 flex items-center gap-3">
                    <span
                      data-testid={`panel-cta-${s.id}`}
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-display font-bold text-sm text-white shadow-arc"
                      style={{ backgroundColor: s.accent }}
                    >
                      {s.cta}
                      <ArrowRight size={16} />
                    </span>
                  </div>
                </div>
              </motion.button>
            );
          })}
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-center gap-3 text-xs text-[#1A1414]/55">
          <span className="px-3 py-1 rounded-full bg-white border border-[#FF8080]/30">
            Mission X • see what isn&apos;t being said
          </span>
          <span className="px-3 py-1 rounded-full bg-white border border-[#FF8080]/30">
            Grades 6–12
          </span>
          <span className="px-3 py-1 rounded-full bg-white border border-[#FF8080]/30">
            Cyber-safety meets cinema
          </span>
        </div>
      </div>
    </div>
  );
}
